import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { pauseOfferResponseSchema } from '@aica/api-types/schemas';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useOrganization, usePermissions } from '@/contexts/RoleContext';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import { formatMinutesInProse } from '@/lib/billing-minutes';
import { WARNING_BANNER_ACTION_CLASS, WarningBanner } from './WarningBanner';

/** Re-read the remaining time once a minute — the banner shows h/m, not seconds. */
const TICK_MS = 60 * 1000;

/**
 * Offer to restore a stalled free org: upgrade inside the window and bonus
 * minutes land on the first month.
 *
 * Every number here comes from the server, which derives them from the moment
 * analysis actually stopped: the deadline, the window length, and the bonus
 * amount. Nothing is hardcoded in the copy, so the banner cannot promise a
 * different offer than the one the payment path will honour, and the countdown
 * cannot be restarted by reloading the page.
 */
export function PauseOfferBanner() {
  const { t } = useTranslation(['common']);
  const { authenticatedFetch } = useAuthFetch();
  const { selectedOrgId } = useOrganization();
  const { canManageProjects } = usePermissions();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Gated on the permission, not just the org: an agent renders this component
  // on every page too, and without the gate each of them fires a billing
  // request per org switch whose answer is discarded on the very next line.
  const { data: offer, isLoading } = useQuery({
    queryKey: ['billing', 'pause-offer', selectedOrgId],
    enabled: !!selectedOrgId && canManageProjects,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const raw = await authenticatedFetch<unknown>(
        API_ENDPOINTS.BILLING_PAUSE_OFFER(selectedOrgId as string)
      );
      const parsed = pauseOfferResponseSchema.safeParse(raw);
      return parsed.success ? parsed.data.offer : null;
    },
  });

  // Only billing-capable roles: an agent who cannot upgrade should not be
  // nagged about a deadline they can do nothing about.
  if (!canManageProjects) return null;
  if (isLoading || !offer?.active || !offer.expiresAt || !offer.pausedAt) return null;

  const msLeft = new Date(offer.expiresAt).getTime() - now;
  // The window closed while the tab stayed open. The server would say the same
  // on the next refetch; don't wait for it to keep showing a dead deadline.
  if (msLeft <= 0) return null;

  const hoursLeft = Math.floor(msLeft / (60 * 60 * 1000));
  const minutesLeft = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000));
  const windowHours = Math.round(
    (new Date(offer.expiresAt).getTime() - new Date(offer.pausedAt).getTime()) /
      (60 * 60 * 1000)
  );

  // Пять состояний, пять предложений — и последнее существует именно потому,
  // что источник может быть НЕИЗВЕСТЕН: ответ старого воркера во время выката
  // приходит без `coveredBy` (схема принимает его намеренно). Валить такой
  // ответ в «идёт за счёт баланса» значит повторить ту самую неправду
  // организации на бонусных минутах с пустым кошельком (находка ревью).
  // Неизвестный источник получает формулировку, которая источника не называет;
  // туда же попадает `contract` — постоплатной организации незачем читать про
  // пулы, которых её договор не касается.
  //
  // Ветка гранта — не косметика. У неё ДРУГОЕ первое предложение: остальные
  // начинаются с «включённые минуты закончились», а здесь они как раз
  // обновились вместе с циклом, и прежний текст утверждал бы обратное
  // тому, что произошло (#500).
  const resumedMessageKey = offer.stillPaused
    ? 'common:pauseOfferBanner.message'
    : offer.coveredBy === 'grant'
      ? 'common:pauseOfferBanner.messageResumedGrant'
      : offer.coveredBy === 'bonus'
        ? 'common:pauseOfferBanner.messageResumedBonus'
        : offer.coveredBy === 'balance'
          ? 'common:pauseOfferBanner.messageResumed'
          : 'common:pauseOfferBanner.messageResumedUnknown';
  // Дробные минуты реальны (чат тратит долю), но в предложении о подарке
  // хвост после запятой только мешает: 163, а не 163.1. Округление вниз при
  // этом не имеет права обнулить положительный остаток — см.
  // `formatMinutesInProse`.
  //
  // Остаток берётся у ТОГО пула, который назван источником: сервер заполняет
  // ровно одно из двух полей, а `left` в текстах — одно и то же имя.
  const leftLabel = formatMinutesInProse(
    (offer.coveredBy === 'grant' ? offer.grantMinutesLeft : offer.bonusMinutesLeft) ?? 0
  );

  /*
    Two sentences, because the offer outlives the pause. Topping up the
    wallet restarts analysis but earns no bonus — that comes from
    upgrading — so the window keeps running while records flow again.
    Telling that customer "analysis is paused" contradicts the records
    appearing on their own dashboard.

    Which of the two resumed states it is matters just as much. The
    single "resumed" sentence named the wallet, and an org whose
    records were covered by bonus minutes read "analysis runs off your
    balance" with $0.00 on the same screen. The source comes from the
    server, which decides it in the same order the pipeline gate does.
  */
  return (
    <WarningBanner
      icon={Clock}
      data-testid="pause-offer-banner"
      message={
        <>
          {t(resumedMessageKey, {
            window: windowHours,
            minutes: offer.bonusMinutes,
            left: leftLabel,
          })}{' '}
          <span className="text-fg-muted">
            {t('common:pauseOfferBanner.timeLeft', {
              hours: hoursLeft,
              mins: minutesLeft,
            })}
          </span>
        </>
      }
      action={
        <Link to="/billing" className={WARNING_BANNER_ACTION_CLASS}>
          {t('common:pauseOfferBanner.cta')}
        </Link>
      }
    />
  );
}
